import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { PageHeader } from "@/components";
import { ErrorPage } from "@/components/ErrorPage/ErrorPage";
import { Loader } from "@/components/Loader/Loader";
import { ErrorPageType } from "@/enum";
import { ROUTE_CONFIG } from "@/routes/config";
import { useSnackBar } from "@/utils/useSnackbar";

import { TicketAttachmentSourceContext } from "@/pages/Private/pages/Support/pages/TicketDetail/hooks/ticketAttachmentSource";
import { ReplyOutcome } from "@/pages/Private/pages/Support/pages/TicketDetail/hooks/useTicketReply";

import { StaffTicketComposer } from "./components/StaffTicketComposer";
import { StaffTicketDetailActions } from "./components/StaffTicketDetailActions";
import { StaffTicketDetailHeader } from "./components/StaffTicketDetailHeader";
import { StaffTicketRail } from "./components/StaffTicketRail";
import { StaffTicketThread } from "./components/StaffTicketThread";
import { STAFF_ATTACHMENT_SOURCE } from "./hooks/staffAttachmentSource";
import { useStaffTicketDetail } from "./hooks/useStaffTicketDetail";
import { TriageOutcome } from "./hooks/useTicketTriage";
import { ComposerMode } from "./utils/staffComposer";

/**
 * One ticket as staff work it, /app/tickets/:id (PREC-3940, spec §7 "Staff"): the full thread with
 * internal notes, the reply / note composer, the triage rail, "Log in as" the company's support
 * user, Previous / Next through the queue page. Reached only through the GLOBAL_ADMIN route; no
 * feature flag anywhere (spec §8). Every read and action lives in `useStaffTicketDetail`; this
 * component lays out, routes, and turns outcomes into snackbars.
 */
export const StaffTicketDetail: FunctionComponent = () => {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const state = useStaffTicketDetail(Number(id));
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	if (state.kind === "NOT_FOUND") {
		return <ErrorPage type={ErrorPageType.NotFound} />;
	}

	if (state.kind === "ERROR") {
		return <ErrorPage type={ErrorPageType.ServerError} />;
	}

	if (state.kind === "LOADING") {
		return <Loader />;
	}

	const { controller } = state;
	const { ticket } = controller;

	const goTo = (ticketId: number) =>
		navigate(`${ROUTE_CONFIG.TICKETS}/${ticketId}`, { state: controller.queueState });

	const reportAttachmentError = (name: string) =>
		handleOpenSnackbar(t("support.detail.attachments.failed", { name }));

	const handleSent = (outcome: ReplyOutcome, mode: ComposerMode) => {
		if (!outcome.ok) {
			handleOpenSnackbar(t("support.staff.detail.composer.failed"));

			return;
		}

		if (mode === "INTERNAL") {
			handleOpenSnackbar(t("support.staff.detail.composer.noteAdded"));

			return;
		}

		handleOpenSnackbar(
			outcome.failedAttachment
				? t("support.staff.detail.composer.sentWithFailedUpload", {
						name: outcome.failedAttachment,
					})
				: t("support.staff.detail.composer.sent")
		);
	};

	const handleTriageOutcome = (outcome: TriageOutcome) => {
		if (outcome.ok) {
			if (outcome.changed) {
				handleOpenSnackbar(t("support.staff.detail.rail.updated"));
			}

			return;
		}

		handleOpenSnackbar(
			t(
				outcome.reason === "ASSIGNEE_NOT_STAFF"
					? "support.staff.detail.rail.assigneeNotStaff"
					: "support.staff.detail.rail.updateFailed"
			)
		);
	};

	const handleLoginAs = async () => {
		if (!(await controller.loginAsSupportUser())) {
			handleOpenSnackbar(t("support.staff.detail.loginFailed"));
		}
	};

	return (
		<TicketAttachmentSourceContext.Provider value={STAFF_ATTACHMENT_SOURCE}>
			<div className="flex h-full w-full flex-col">
				<PageHeader
					actions={
						<StaffTicketDetailActions
							isLoggingIn={controller.isLoggingIn}
							neighbours={controller.neighbours}
							showArrows={controller.queueState !== null}
							supportUser={controller.supportUser}
							onLoginAs={() => void handleLoginAs()}
							onNavigate={goTo}
						/>
					}
					title={
						<span className="flex items-center gap-1.5">
							<Link className="text-text-meta hover:text-text" to={ROUTE_CONFIG.TICKETS}>
								{t("support.staff.detail.breadcrumb")}
							</Link>
							<span className="text-text-meta">›</span>
							<span>{t("support.detail.ticketNumber", { id: ticket.id })}</span>
						</span>
					}
				/>

				<div className="flex min-h-0 flex-1 gap-6">
					<section className="flex min-w-0 flex-1 flex-col gap-4">
						<StaffTicketDetailHeader ticket={ticket} />
						<div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1">
							<StaffTicketThread ticket={ticket} onAttachmentError={reportAttachmentError} />
						</div>
						<StaffTicketComposer ticket={ticket} onSent={handleSent} />
					</section>
					<StaffTicketRail
						controller={controller}
						onAttachmentError={reportAttachmentError}
						onLoginAs={() => void handleLoginAs()}
						onTriageOutcome={handleTriageOutcome}
					/>
				</div>
				{renderMessageSnackbar()}
			</div>
		</TicketAttachmentSourceContext.Provider>
	);
};
